import React from "react";
import { EuiIcon, EuiText, EuiTitle, IconType } from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import { ContactTopics } from "./contactForm";

// Kept for backwards-compatibility: services.tsx imports this shape.
export interface AutonubilHomeFeature {
  id: ContactTopics;
  icon: IconType;
  title: string;
  description: string;
  points?: string[];
}

// The Features section is intentionally all about the opsanio appliance - its
// core capability modules - so the homepage focus stays on the product. The
// broader autonubil services (DevOps, SSO, Nextcloud) live in the Services
// section further down.
interface OpsanioCapability {
  slug: string;
  icon: IconType;
  title: string;
  description: string;
  points: string[];
  // Spans two grid columns (full row on one column) - used for the closing card
  // so the grid stays even.
  wide?: boolean;
}

const C = {
  accent: "var(--ak-accent)",
  accentLight: "var(--ak-accent-tint)",
  accentBorder: "var(--ak-accent-border)",
  pageBg: "var(--ak-page-bg)",
  cardBg: "var(--ak-surface)",
  border: "var(--ak-border)",
  textPrimary: "var(--ak-text)",
  textSecondary: "var(--ak-text-secondary)",
  textMuted: "var(--ak-text-muted)",
  sans: "system-ui,-apple-system,sans-serif",
  radius: 14,
};

const CheckIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{ flexShrink: 0, marginTop: 2 }}
  >
    <path
      d="M5 13l4 4L19 7"
      stroke="var(--ak-accent)"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CapabilityCard: React.FC<{ capability: OpsanioCapability }> = ({
  capability,
}) => {
  // Not clickable -> deliberately no hover effect (a hover would imply a link).
  return (
    <div
      style={{
        background: C.cardBg,
        border: `1px solid ${C.border}`,
        borderRadius: C.radius,
        padding: "30px 28px",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 1px 4px var(--ak-shadow)",
        position: "relative",
        overflow: "hidden",
      }}
      className={capability.wide ? "ak-cap-wide" : undefined}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
        <div
          style={{
            width: 56,
            height: 56,
            flexShrink: 0,
            background: C.accentLight,
            border: `1px solid ${C.accentBorder}`,
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <EuiIcon
            type={capability.icon}
            size="xl"
            color="primary"
            style={{ maxWidth: 32, maxHeight: 32 }}
          />
        </div>
        <EuiTitle size="xs">
          <h3
            style={{
              color: C.textPrimary,
              fontWeight: 700,
              fontSize: "1.15rem",
              letterSpacing: "-0.01em",
              margin: 0,
            }}
          >
            {capability.title}
          </h3>
        </EuiTitle>
      </div>

      <EuiText size="s" color="subdued">
        <p style={{ margin: 0, lineHeight: 1.65, color: C.textSecondary }}>
          {capability.description}
        </p>
      </EuiText>

      <div style={{ height: 1, background: C.border, margin: "20px 0 16px" }} />

      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {capability.points.map((point) => (
          <li
            key={point}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              fontSize: "0.9rem",
              lineHeight: 1.5,
              color: C.textSecondary,
              fontWeight: 500,
            }}
          >
            <CheckIcon />
            <span>{point}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const AutonubilFeatures = (): JSX.Element => {
  const { translate } = useAnketaI18n();
  const i18nPrefix = "anketa.autonubil.home.features";
  const cp = `${i18nPrefix}.cap`;

  const pts = (slug: string, fallbacks: string[]): string[] =>
    fallbacks.map((fb, i) =>
      translate(`${cp}.${slug}.point${i + 1}`, { defaultMessage: fb } as any)
    );

  // One card per opsanio dashboard / capability area. asset, vuln and compliance
  // reuse the original (already-translated) keys; software, siemwazuh, cert,
  // identity, network, ai and container use new keys (EN + DE added to the catalogs).
  // The ai card covers the rimor-plugin-skill-* chat skills.
  const capabilities: OpsanioCapability[] = [
    {
      slug: "asset",
      icon: "database",
      title: translate(`${cp}.asset.title`, { defaultMessage: "Asset Management" } as any),
      description: translate(`${cp}.asset.description`, {
        defaultMessage:
          "Every device, VM, account and service - discovered across 40+ systems and merged into one sovereign data model.",
      } as any),
      points: pts("asset", [
        "Cross-source deduplication (Codex model)",
        "Full provenance per asset",
        "Field-level change detection",
        "Agent-based and agentless discovery",
      ]),
    },
    {
      slug: "software",
      icon: "package",
      title: translate(`${cp}.software.title`, { defaultMessage: "Software Management" } as any),
      description: translate(`${cp}.software.description`, {
        defaultMessage:
          "Every installed package and OS across the estate - with end-of-life, update and upgrade status.",
      } as any),
      points: pts("software", [
        "Vendor / product / version inventory",
        "End-of-life & end-of-support flags",
        "Update & upgrade tracking",
      ]),
    },
    {
      slug: "container",
      icon: "layers",
      title: translate(`${cp}.container.title`, { defaultMessage: "Container Images" } as any),
      description: translate(`${cp}.container.description`, {
        defaultMessage:
          "Every container image in Kubernetes, Docker, Podman and containerd - with its SBOM, CVEs, lifecycle and the systems it runs on.",
      } as any),
      points: pts("container", [
        "Kubernetes agent plus Docker, Podman & containerd hosts",
        "SBOM per image (CycloneDX via syft)",
        "CVEs & end-of-life status per image",
        "Registry, tags and where each image runs",
      ]),
    },
    {
      slug: "vuln",
      icon: "bug",
      title: translate(`${cp}.vuln.title`, { defaultMessage: "Vulnerability Management" } as any),
      description: translate(`${cp}.vuln.description`, {
        defaultMessage:
          "Know which of the critical few to fix first - CVEs enriched and prioritised automatically.",
      } as any),
      points: pts("vuln", [
        "CPE → NVD / CVE mapping",
        "EPSS & CISA KEV scoring",
        "Prioritised remediation",
      ]),
    },
    {
      slug: "siemwazuh",
      icon: "watchesApp",
      title: translate(`${cp}.siemwazuh.title`, { defaultMessage: "SIEM / Wazuh" } as any),
      description: translate(`${cp}.siemwazuh.description`, {
        defaultMessage:
          "Integrated, managed Wazuh SIEM - real-time threat detection, correlation and response.",
      } as any),
      points: pts("siemwazuh", [
        "Integrated, operated Wazuh SIEM",
        "Real-time event correlation",
        "MITRE ATT&CK mapping",
        "Managed Sigma rules with a built-in rule editor",
      ]),
    },
    {
      slug: "cert",
      icon: "lock",
      title: translate(`${cp}.cert.title`, { defaultMessage: "Certificate Management" } as any),
      description: translate(`${cp}.cert.description`, {
        defaultMessage:
          "Track every certificate and its expiry - never get caught out by an expired cert again.",
      } as any),
      points: pts("cert", [
        "Expiry tracking (expired / soon / safe)",
        "Discovered across the estate",
        "DNS & issuer details",
        "CRL / OCSP revocation checking",
      ]),
    },
    {
      slug: "identity",
      icon: "users",
      title: translate(`${cp}.identity.title`, { defaultMessage: "Identity Management" } as any),
      description: translate(`${cp}.identity.description`, {
        defaultMessage:
          "Accounts from 20+ sources - directories, endpoints, cloud and infrastructure - merged into one identity per person.",
      } as any),
      points: pts("identity", [
        "Directories: AD / LDAP, Entra ID, Keycloak",
        "Local accounts from endpoints, hypervisors & firewalls",
        "One identity per person across all accounts",
        "Who logged on where - from Wazuh logon events",
      ]),
    },
    {
      slug: "network",
      icon: "globe",
      title: translate(`${cp}.network.title`, { defaultMessage: "Network" } as any),
      description: translate(`${cp}.network.description`, {
        defaultMessage:
          "East-west and north-south traffic from IPFIX, NetFlow and sFlow - mapped geographically and by AS.",
      } as any),
      points: pts("network", [
        "IPFIX / NetFlow v9 / sFlow",
        "Geographic & AS-level top talkers",
        "Synthetic flows from logs",
      ]),
    },
    {
      slug: "ai",
      icon: "sparkleFilled",
      title: translate(`${cp}.ai.title`, { defaultMessage: "AI Security Skills" } as any),
      description: translate(`${cp}.ai.description`, {
        defaultMessage:
          "Ask the dashboard chat about any asset, CVE or finding - answered from your own opsanio data.",
      } as any),
      points: pts("ai", [
        "Asset risk briefs & hardening advice",
        "CVE enrichment & threat analysis",
        "Auditor-ready compliance posture reports",
      ]),
    },
    {
      slug: "remote",
      icon: "console",
      title: translate(`${cp}.remote.title`, { defaultMessage: "Remote Access (Jump Host)" } as any),
      description: translate(`${cp}.remote.description`, {
        defaultMessage:
          "Open an RDP or SSH session to any discovered asset straight from the browser - the appliance is your jump host, with nothing to install.",
      } as any),
      points: pts("remote", [
        "RDP & SSH in the browser (Apache Guacamole)",
        "Launched from the asset's detail view",
        "Access for authorised roles only",
        "Every session logged - who, where, how long",
      ]),
    },
    {
      slug: "compliance",
      icon: "reportingApp",
      title: translate(`${cp}.compliance.title`, {
        defaultMessage: "Compliance",
      } as any),
      description: translate(`${cp}.compliance.description`, {
        defaultMessage:
          "Map your estate to NIS2, BSI and ISO 27001 automatically - and keep every byte on-premises.",
      } as any),
      points: pts("compliance", [
        "Auto-mapping to NIS2 / BSI / ISO 27001",
        "Opsane Score (0-100)",
        "Audit-ready evidence",
        "On-prem - no data egress",
      ]),
      wide: true,
    },
  ];

  return (
    <div
      style={{
        background: C.pageBg,
        padding: "72px clamp(24px, 5vw, 48px)",
        borderTop: `1px solid ${C.border}`,
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: C.accent,
              marginBottom: 10,
            }}
          >
            {translate(`${i18nPrefix}.eyebrow`, {
              defaultMessage: "The opsanio appliance",
            } as any)}
          </div>
          <div style={{ width: "fit-content", maxWidth: "100%", margin: "0 auto" }}>
            <EuiTitle size="l">
              <h2
                style={{
                  color: C.textPrimary,
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  marginBottom: 0,
                }}
              >
                {translate(`${i18nPrefix}.title`, {
                  defaultMessage: "One appliance. Full visibility.",
                } as any)}
              </h2>
            </EuiTitle>
            <div
              style={{
                width: "100%",
                height: 3,
                marginTop: 14,
                background: `linear-gradient(90deg, ${C.accent} 0%, var(--ak-accent-2) 100%)`,
                borderRadius: 2,
              }}
            />
          </div>
          <p
            style={{
              maxWidth: 600,
              margin: "16px auto 0",
              color: C.textSecondary,
              fontSize: "clamp(0.97rem, 1.6vw, 1.1rem)",
              lineHeight: 1.65,
            }}
          >
            {translate(`${i18nPrefix}.subtitle`, {
              defaultMessage:
                "opsanio consolidates the security capabilities you would otherwise stitch together from many tools - into one sovereign, managed appliance.",
            } as any)}
          </p>
        </div>

        {/* The wide card spans two columns so it closes the last row: next to the
            10th card on 3 columns, on its own row on 2, full width on 1. */}
        <style>
          {`
            .ak-cap-grid { container-type: inline-size; }
            .ak-cap-wide { grid-column: 1 / -1; }
            @container (min-width: 740px) { .ak-cap-wide { grid-column: span 2; } }
          `}
        </style>
        <div
          className="ak-cap-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
            gap: 20,
            alignItems: "stretch",
          }}
        >
          {capabilities.map((capability) => (
            <CapabilityCard key={capability.slug} capability={capability} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AutonubilFeatures;
